/**
 * ChildScenariosTable — damage scenarios inherited from child TARAs
 * in hierarchical mode. Read-only data + two interactive columns:
 *   - Relevant       (checkbox)
 *   - Justification  (text input)
 *
 * Modifications are owned by the parent HierarchicalScenarioReview
 * via `useHierarchicalApprove`; this table just renders rows and
 * dispatches change events.
 *
 * The damage_scenario_agent does not tag rows with `source`, so two
 * child TARAs can both emit `DS-001`. Like ScenarioTable we inject a
 * synthetic `_rowKey` for stable React reconciliation.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
} from '../../../../components/ui/DataTable';
import LevelBadge from '../../../../components/ui/LevelBadge';
import RelevanceCheckmark from '../../components/RelevanceCheckmark';
import RelevanceEditToggle from '../../components/RelevanceEditToggle';
import { getImpactColor } from '../../../../utils/severityStyle';
import type { ScenarioRecord } from './scenarioFieldSchema';
import styles from './ChildScenariosTable.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

const renderImpact = (row: ChildScenarioView) =>
  row.final_impact_rating ? (
    <LevelBadge
      color={getImpactColor(row.final_impact_rating, { scheme: 'fourTier' })}
      label={String(row.final_impact_rating)}
      size="sm"
    />
  ) : (
    ''
  );

export interface ChildScenarioView extends ScenarioRecord {
  is_relevant?: boolean;
  relevance_justification?: string;
}

export interface ChildScenariosTableProps {
  scenarios: ChildScenarioView[];
  onToggleRelevance: (damageId: string) => void;
  onJustificationChange: (damageId: string, justification: string) => void;
  disabled?: boolean;
  /** Persist relevance modifications on exit-edit. Story 15.F. */
  onSave?: () => Promise<void> | void;
}

export default function ChildScenariosTable({
  scenarios,
  onToggleRelevance,
  onJustificationChange,
  disabled,
  onSave,
}: ChildScenariosTableProps) {
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const liveEditing = editing && !disabled && !saving;

  const handleToggle = async () => {
    if (editing) {
      if (onSave) {
        setSaving(true);
        try { await onSave(); } finally { setSaving(false); }
      }
      setEditing(false);
    } else {
      setEditing(true);
    }
  };

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scenarios;
    return scenarios.filter((r) =>
      Object.values(r).some((v) =>
        v == null
          ? false
          : Array.isArray(v)
          ? v.join(' ').toLowerCase().includes(q)
          : String(v).toLowerCase().includes(q),
      ),
    );
  }, [scenarios, search]);

  const totalPages = Math.max(1, Math.ceil(searched.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => searched.slice((page - 1) * pageSize, page * pageSize),
    [searched, page, pageSize],
  );

  // Position-stable synthetic key — child TARAs can collide on
  // damage_id, so we suffix with the absolute index.
  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.damage_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(
    () => [
      { key: 'damage_id', label: 'ID', sortable: true, width: '8%' },
      {
        key: 'asset_ids',
        label: 'Assets',
        sortable: false,
        width: '15%',
        render: (row: ChildScenarioView) => joinList(row.asset_ids),
      },
      { key: 'damage_scenario', label: 'Scenario', sortable: false, width: '38%' },
      {
        key: 'final_impact_rating',
        label: 'Impact',
        sortable: true,
        width: '10%',
        render: renderImpact,
      },
      {
        key: '_relevance',
        label: 'Relevant',
        sortable: false,
        align: 'center',
        width: '10%',
        render: (row: ChildScenarioView) =>
          liveEditing ? (
            <input
              type="checkbox"
              className={styles.relevanceCheckbox}
              checked={row.is_relevant !== false}
              onChange={() => onToggleRelevance(row.damage_id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Mark ${row.damage_id} as relevant`}
            />
          ) : (
            <RelevanceCheckmark relevant={row.is_relevant !== false} />
          ),
      },
      {
        key: '_justification',
        label: 'Justification',
        sortable: false,
        width: '19%',
        render: (row: ChildScenarioView) =>
          liveEditing ? (
            <input
              type="text"
              className={styles.justificationInput}
              value={row.relevance_justification ?? ''}
              placeholder="Why? (optional)"
              onChange={(e) => onJustificationChange(row.damage_id, e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span>{row.relevance_justification || '—'}</span>
          ),
      },
    ],
    [onToggleRelevance, onJustificationChange, liveEditing],
  );

  return (
    <DataTableAny
      columns={columns}
      data={keyed}
      rowKey="_rowKey"
      emptyMessage="No inherited damage scenarios from child TARAs."
      toolbar={
        <TableToolbarAny
          search={{
            value: search,
            onChange: (v: string) => { setSearch(v); setPage(1); },
            placeholder: 'Search inherited scenarios…',
          }}
          count={{ value: searched.length, label: 'Inherited Scenarios' }}
          showFilterButton={false}
          actions={
            <RelevanceEditToggle
              editing={liveEditing}
              onToggle={handleToggle}
              pending={saving}
              disabled={disabled}
            />
          }
        />
      }
      pagination={
        <TablePaginationAny
          currentPage={page}
          totalItems={searched.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      }
    />
  );
}
